import {
  faIdCard,
  faPaperclip,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { useMediaQuery, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { TrlFormHelperText } from '»components/atoms/TrlFormHelperText';
import { onboardingSlice } from '»components/Auth';
import {
  onboardingDocumentsSelector,
  onboardingMandatoryAndIdentificationSelector,
  onboardingOpeningRequestIdSelector,
} from '»components/Auth/Onboarding/redux/onboarding.selectors';
import { TrlFeedbackModal } from '»components/molecules/TrlFeedbackModal';
import { useAlertModalStatus } from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { TrlFooterLink } from '../../TrlFooterLink';
import { TrlFieldContainer } from '../TrlFieldContainer';
import {
  FileFieldContainer,
  FileInputBox,
  IconButtonStyled,
  IconStyled,
  InsideBox,
  MenuBox,
  NameBox,
  OverlayBox,
  VisuallyHiddenInput,
} from './TrlFileInput.styles';

export const TrlFileInput = ({
  documentRecord,
  index,
  name,
  isIdDocument,
  isIdDocumentNotMandatory,
}) => {
  const mobileInputs = ['take_photo', 'upload_photo', 'upload_pdf'];
  const [file, setFile] = useState(documentRecord?.values?.file || null);
  const [menuOpen, setMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const fileInputRef = useRef();
  const alertModalStatus = useAlertModalStatus(false);
  const menuRef = useRef(null);
  const dispatch = useDispatch();

  const { values: formValues, setFieldValue } = useFormikContext();

  const openingRequestId = useSelector(onboardingOpeningRequestIdSelector);
  const mandatoryAndIdentification = useSelector(
    onboardingMandatoryAndIdentificationSelector,
  );
  const documents = useSelector(onboardingDocumentsSelector);

  const isRequired = documentRecord?.mandatory === 'S';

  const disabledCursor = !!file;

  const identificationDocCodes = documents
    .filter((document) => document.identification === 'S')
    .map((document) => document.documentCode);

  const otherIdDocHasValue = Object.entries(formValues).some(
    ([documentCode, documentInfo]) => {
      const isIdentification = identificationDocCodes.includes(documentCode);

      return (
        isIdentification &&
        documentInfo.file &&
        documentCode !== documentRecord.documentCode
      );
    },
  );

  const feedbackModalTitle = 'TRLT_UI-DELETE_DOCUMENT';

  const delDocAlertTexts = {
    title: feedbackModalTitle,
    descr: {
      content: 'TRLT_UI-DELETE_DOCUMENT_DESC',
      values: { fileName: file ? `"${file.name}"` : null },
    },
  };

  const isDeleteDocAlert =
    alertModalStatus.descr.content === delDocAlertTexts.descr.content;

  const dangerousExtensions = [
    'exe',
    'bat',
    'sh',
    'cmd',
    'vbs',
    'msi',
    'jar',
    'ps1',
    'scr',
  ];

  const allowedFileTypes = {
    'image/png': {
      signature: '89504E470D0A1A0A',
      maxSizeMB: 2,
    },
    'image/jpeg': {
      signature: 'FFD8FF',
      maxSizeMB: 2,
    },
    'application/pdf': {
      signature: '25504446',
      maxSizeMB: 5,
    },
  };

  const allowedImageMimeTypes = Object.keys(allowedFileTypes)
    .filter((type) => type.startsWith('image'))
    .join(', ');

  const allowedApplicationMimeTypes = Object.keys(allowedFileTypes)
    .filter((type) => type.startsWith('application'))
    .join(', ');

  const handleFileChange = async (event) => {
    try {
      const selectedFile = event.target.files[0];

      const selectedFileInfo = {
        name: selectedFile.name,
        sizeMB: Number((selectedFile.size / (1024 * 1024)).toFixed(2)),
        type: selectedFile.type,
        extension: selectedFile.name.split('.').pop().toLowerCase(),
      };

      if (dangerousExtensions.includes(selectedFileInfo.extension)) {
        alertModalStatus.onChangeStatus({
          isOpen: true,
          title: feedbackModalTitle,
          descr: {
            content: 'TRLT_UI-INSECURE_FILE',
          },
        });
        event.target.value = '';
        return;
      }

      const fileTypeConfig = allowedFileTypes[selectedFileInfo.type];
      if (!fileTypeConfig) {
        alertModalStatus.onChangeStatus({
          isOpen: true,
          title: feedbackModalTitle,
          descr: {
            content: 'TRLT_UI-INVALID_FILE_TYPE',
          },
        });
        event.target.value = '';
        return;
      }

      if (selectedFileInfo.sizeMB > fileTypeConfig.maxSizeMB) {
        alertModalStatus.onChangeStatus({
          isOpen: true,
          title: feedbackModalTitle,
          descr: {
            content: 'TRLT_UI-FILE_TOO_LARGE',
          },
        });
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const bytes = new Uint8Array(e.target.result).slice(0, 8);
        const hexSignature = Array.from(bytes)
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('')
          .toUpperCase();

        const isValidSignature = hexSignature.startsWith(
          fileTypeConfig.signature,
        );

        if (!isValidSignature) {
          alertModalStatus.onChangeStatus({
            isOpen: true,
            title: feedbackModalTitle,
            descr: {
              content: 'TRLT_UI-INVALID_FILE_CONTENT',
            },
          });
          event.target.value = '';
          return;
        }

        setFile(selectedFile);
        setFieldValue(name, selectedFile);
      };

      reader.readAsArrayBuffer(selectedFile.slice(0, 8));
    } catch {}

    if (isMobile) setMenuOpen(!menuOpen);
  };

  const handleFileDelete = () => {
    alertModalStatus.onChangeStatus({
      isOpen: true,
      ...delDocAlertTexts,
    });
  };

  const deleteFile = () => {
    alertModalStatus.onChangeStatus({ isOpen: false });
    setFile(null);
    if (documentRecord?.values) {
      const { expirationDate, docNumber, lifetime, noteId, filename } =
        documentRecord.values;
      dispatch(
        onboardingSlice.actions.submitDocumentRequest({
          openingRequestId,
          documentCode: documentRecord.documentCode,
          deleteDoc: 'S',
          expirationDate,
          docNumber,
          lifetime,
          noteId,
          index,
          setFieldValue,
          filename,
        }),
      );
    }
    setFieldValue(documentRecord.documentCode, {
      file: null,
      documentNumber: '',
      lifetime: '',
      expirationDate: '',
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleOpenMenu = () => {
    if (isMobile && !file) setMenuOpen(!menuOpen);
    else fileInputRef.current.click();
  };

  const handleSelectClick = (input) => {
    if (input === 'take_photo') {
      fileInputRef.current.setAttribute('capture', 'environment');
    } else {
      fileInputRef.current.removeAttribute('capture');
    }
    fileInputRef.current.setAttribute('accept', getInputDetails(input).accept);
    fileInputRef.current.click();
  };

  useEffect(() => {
    if (menuOpen && file) {
      const event = new Event('change', { bubbles: true });
      fileInputRef.current.dispatchEvent(event);
      setMenuOpen(!menuOpen);
    }
  }, [file, menuOpen]);

  const getInputDetails = (input) => {
    switch (input) {
      case 'take_photo':
        return {
          label: 'TRLT_UI-TAKE_A_PHOTO',
          action: 'openCamera',
          accept: allowedImageMimeTypes,
        };
      case 'upload_photo':
        return {
          label: 'TRLT_UI-UPLOAD_PHOTO',
          accept: allowedImageMimeTypes,
        };
      case 'upload_pdf':
        return {
          label: 'TRLT_UI-UPLOAD_DOCUMENT',
          accept: allowedApplicationMimeTypes,
        };
      default:
        return {
          label: 'TRLT_UI-UPLOAD_DOCUMENT',
          accept: allowedImageMimeTypes,
        };
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <TrlFieldContainer
      label={documentRecord.webDescription}
      isRequired={isRequired || isIdDocumentNotMandatory}
    >
      <FileFieldContainer>
        <FileInputBox
          backgroundColor={
            file
              ? theme.palette.colors.secondaryBackground
              : theme.palette.colors.primaryBackground
          }
          disabledCursor={disabledCursor}
        >
          <>
            {isIdDocument && (
              <IconButtonStyled
                aria-label={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-ID_DOCUMENT',
                })}
                onClick={handleOpenMenu}
                disabledCursor={disabledCursor}
                disabled={!!file}
              >
                <IconStyled size={'16px'} icon={faIdCard} />
              </IconButtonStyled>
            )}
            <InsideBox onClick={handleOpenMenu}>
              <NameBox isPlaceholder={!file}>
                {file ? (
                  file.name
                ) : (
                  <FormattedMessage id="TRLT_UI-DOC_FIELD_PLACEHOLDER" />
                )}
              </NameBox>
            </InsideBox>
            {file ? (
              <IconButtonStyled
                onClick={handleFileDelete}
                aria-label={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-DELETE_DOCUMENT',
                })}
              >
                <IconStyled size={'14px'} icon={faXmark} />
              </IconButtonStyled>
            ) : (
              <IconButtonStyled
                onClick={handleOpenMenu}
                aria-label={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-UPLOAD_DOCUMENT',
                })}
              >
                <IconStyled size={'14px'} icon={faPaperclip} />
              </IconButtonStyled>
            )}
            {menuOpen ? (
              <OverlayBox>
                <MenuBox
                  className="menu"
                  ref={menuRef}
                  onClick={(e) => e.stopPropagation()}
                >
                  {!file &&
                    mobileInputs &&
                    mobileInputs.map((input) => (
                      <Fragment key={input}>
                        <TrlFooterLink
                          label={getInputDetails(input).label}
                          backgroundColor={theme.palette.colors.infoBackground}
                          height="39px"
                          navigateTo={() => handleSelectClick(input)}
                          hasBorder
                          justifyContent="center"
                          fontSize="12px"
                          isTextBold
                          borderRadius="0"
                        />
                        <VisuallyHiddenInput
                          ref={fileInputRef}
                          type="file"
                          onChange={handleFileChange}
                          disabled={!!file}
                        />
                      </Fragment>
                    ))}
                </MenuBox>
              </OverlayBox>
            ) : (
              <VisuallyHiddenInput
                ref={fileInputRef}
                type="file"
                accept={Object.keys(allowedFileTypes).join(', ')}
                onChange={handleFileChange}
                disabled={!!file}
              />
            )}
          </>
          <TrlFeedbackModal
            title={{ content: alertModalStatus.title }}
            descr={alertModalStatus.descr}
            type="warning"
            mainActionBtnNameExp={isDeleteDocAlert ? 'TRLT_UI-CONTINUE' : null}
            open={alertModalStatus.isOpen}
            onClose={() => {
              alertModalStatus.onChangeStatus({ isOpen: false });
            }}
            mainActionMethod={isDeleteDocAlert ? deleteFile : null}
          />
        </FileInputBox>
        {isIdDocument && !mandatoryAndIdentification && (
          <TrlFormHelperText
            helperType={
              !mandatoryAndIdentification && otherIdDocHasValue && !file
                ? 'inactive'
                : 'info'
            }
            helperText={TrlFormattedMessagePlain({
              id: 'TRLT_UI-ID_DOCUMENT_WARNING',
            })}
          />
        )}
      </FileFieldContainer>
    </TrlFieldContainer>
  );
};

TrlFileInput.propTypes = {
  documentRecord: PropTypes.object,
  index: PropTypes.number,
  name: PropTypes.string,
  isIdDocument: PropTypes.bool,
  isIdDocumentNotMandatory: PropTypes.bool,
};
